import React from "react";
import Link from "next/link";
import Icon from "./Icon";
import Button from "./Button";
import renderIcon from "../../../components/renderIcon";
import { applyLang } from "../../../i18n";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { resolveImage, resolveUrl } from "../utils";
import { isExternalUrl, buildLinkHref } from "../../../components/normalizeMenuUrl";
import { readHeroSlides } from "../compat";

const heroStyles = `
.t26-hero {
  padding: clamp(20px, 3.5vw, 36px) clamp(14px, 2.4vw, 32px) 8px;
}
.t26-hero__frame {
  position: relative;
  width: 100%;
  max-width: calc(var(--container) + 80px);
  margin-inline: auto;
  height: clamp(380px, 56vw, 580px);
  overflow: hidden;
  isolation: isolate;
  background: #0c1217;
  border-radius: clamp(18px, 2.2vw, 28px);
  box-shadow:
    0 30px 60px -28px rgba(8,14,22,.45),
    0 12px 28px -16px rgba(8,14,22,.30);
}

/* Slides : cross-fade + zoom Ken-Burns */
.t26-hero__slide {
  position: absolute; inset: 0;
  opacity: 0;
  transform: scale(1.05);
  transition: opacity 1.1s ease, transform 6s ease;
}
.t26-hero__slide.is-active {
  opacity: 1;
  transform: scale(1);
}
.t26-hero__slide img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
}
.t26-hero__wash {
  position: absolute; inset: 0;
  background:
    radial-gradient(120% 80% at 50% 100%, rgba(7,12,18,.80) 0%, rgba(7,12,18,.30) 50%, rgba(7,12,18,.10) 100%),
    linear-gradient(180deg, rgba(7,12,18,.45) 0%, rgba(7,12,18,0) 35%, rgba(7,12,18,.55) 100%);
}

.t26-hero__content {
  position: absolute; inset: 0; z-index: 2;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 24px clamp(20px, 5vw, 56px);
  text-align: center;
  color: #fff;
  pointer-events: none;
}
.t26-hero__content > * { pointer-events: auto; }

/* Animations d'entrée en cascade */
.t26-hero__content--enter .t26-hero__eyebrow { animation: t26-enter 1s cubic-bezier(.16,1,.3,1) .15s both; }
.t26-hero__content--enter .t26-hero__title   { animation: t26-enter 1.05s cubic-bezier(.16,1,.3,1) .35s both; }
.t26-hero__content--enter .t26-hero__sub     { animation: t26-enter .95s cubic-bezier(.16,1,.3,1) .5s both; }
.t26-hero__content--enter .t26-hero__desc    { animation: t26-enter .9s cubic-bezier(.16,1,.3,1) .65s both; }
.t26-hero__content--enter .t26-hero__ctas    { animation: t26-enter .9s cubic-bezier(.16,1,.3,1) .8s both; }

@keyframes t26-enter {
  from { opacity: 0; transform: translateY(28px); filter: blur(5px); }
  to   { opacity: 1; transform: translateY(0);    filter: blur(0); }
}

.t26-hero__eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 7px 16px;
  background: rgba(255,255,255,.10);
  border: 1px solid rgba(255,255,255,.22);
  backdrop-filter: blur(8px);
  border-radius: var(--r-pill);
  font-size: 11px; font-weight: 700;
  letter-spacing: .22em; text-transform: uppercase;
  color: #fff;
}

.t26-hero__title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: clamp(34px, 5.8vw, 68px);
  line-height: 1.02;
  letter-spacing: -.025em;
  margin: 18px 0 0;
  text-wrap: balance;
  max-width: 16ch;
  text-shadow: 0 2px 28px rgba(0,0,0,.35);
}
.t26-hero__title em {
  display: block;
  font-style: italic;
  color: color-mix(in oklab, white 65%, var(--secondary) 35%);
  font-weight: 500;
  font-size: .6em;
  letter-spacing: .01em;
  margin-top: 10px;
  opacity: .92;
}

.t26-hero__sub {
  margin: 14px 0 0;
  font-size: clamp(16px, 1.6vw, 19px);
  color: rgba(255,255,255,.86);
  text-wrap: pretty;
  max-width: 36ch;
}

.t26-hero__desc {
  margin: 12px 0 0;
  font-size: 15px;
  color: rgba(255,255,255,.74);
  max-width: 56ch;
  text-wrap: pretty;
}

.t26-hero__ctas {
  margin-top: 24px;
  display: inline-flex; flex-wrap: wrap; justify-content: center; gap: 10px;
}
/* Force la lisibilité des boutons sur image sombre, quelle que soit la
   couleur primary/secondary configurée par l'admin */
.t26-hero__ctas .t26-btn--primary,
.t26-hero__ctas .t26-btn--secondary {
  color: #fff;
}

/* Counter top-right */
.t26-hero__count {
  position: absolute; z-index: 3;
  top: clamp(18px, 3vw, 28px);
  inset-inline-end: clamp(20px, 5vw, 56px);
  font-family: var(--font-display);
  font-size: 13px; font-weight: 700;
  color: rgba(255,255,255,.85);
  letter-spacing: .12em;
}
.t26-hero__count b { font-size: 22px; color: #fff; }
.t26-hero__count i { font-style: normal; opacity: .55; margin: 0 6px; }

/* Bottom controls */
.t26-hero__ctrl {
  position: absolute; z-index: 3;
  inset-inline: clamp(20px, 5vw, 56px);
  bottom: clamp(20px, 4vw, 36px);
  display: flex; align-items: center; justify-content: space-between;
  pointer-events: none;
}
.t26-hero__dots { display: inline-flex; gap: 8px; align-items: center; pointer-events: auto; }
.t26-hero__dot {
  width: 28px; height: 4px;
  border-radius: 2px;
  background: rgba(255,255,255,.32);
  border: 0; padding: 0; cursor: pointer;
  transition: width .25s ease, background .25s ease;
}
.t26-hero__dot.is-active {
  width: 48px;
  background: var(--secondary);
}
.t26-hero__arrows { display: inline-flex; gap: 8px; pointer-events: auto; }
.t26-hero__arrow {
  width: 44px; height: 44px;
  border-radius: 50%;
  background: rgba(255,255,255,.10);
  border: 1px solid rgba(255,255,255,.28);
  backdrop-filter: blur(8px);
  color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: background .15s ease, border-color .15s ease;
}
.t26-hero__arrow:hover { background: rgba(255,255,255,.20); border-color: #fff; }

/* === Fallback (pas de slides configurés) === */
.t26-hero--fallback .t26-hero__frame {
  height: clamp(280px, 38vw, 420px);
  background:
    radial-gradient(60% 70% at 50% 30%, color-mix(in oklab, var(--secondary) 30%, transparent) 0%, transparent 100%),
    linear-gradient(135deg, color-mix(in oklab, var(--primary) 90%, black) 0%, color-mix(in oklab, var(--primary) 70%, black) 100%);
}
.t26-hero--fallback .t26-hero__title { max-width: 24ch; }
.t26-hero--fallback .t26-hero__title em {
  color: color-mix(in oklab, white 60%, var(--secondary) 40%);
}

@media (max-width: 640px) {
  .t26-hero__frame { height: 62vh; }
  .t26-hero__title { font-size: clamp(28px, 8.5vw, 44px); max-width: 100%; }
  .t26-hero__sub   { font-size: 15px; }
  .t26-hero__desc  { font-size: 13px; max-width: 90%; }
  .t26-hero__count { font-size: 11px; padding: 4px 10px; }
}
`;

function HeroCta({ org, label, url, icon, variant }) {
    if (!label) return null;
    const iconNode = icon ? renderIcon(icon, 16) : null;

    if (!url) {
        return <Button variant={variant} size="lg" as="button">{iconNode}{label}</Button>;
    }
    if (isExternalUrl(url, org)) {
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <Button variant={variant} size="lg" as="span">{iconNode}{label}</Button>
            </a>
        );
    }
    return (
        <Link href={buildLinkHref(resolveUrl(org, url), org)}>
            <Button variant={variant} size="lg" as="span">{iconNode}{label}</Button>
        </Link>
    );
}

function SlideContent({ slide, org, contentKey }) {
    const eyebrowIcon = slide.eyebrow_icon ? renderIcon(slide.eyebrow_icon, 14) : null;

    return (
        <div className="t26-hero__content t26-hero__content--enter" key={contentKey}>
            {slide.eyebrow && (
                <span className="t26-hero__eyebrow">
                    {eyebrowIcon}
                    <b>{slide.eyebrow}</b>
                </span>
            )}
            {slide.title && (
                <h1 className="t26-hero__title">
                    {slide.title}
                    {slide.subtitle ? <> <em>{slide.subtitle}</em></> : null}
                </h1>
            )}
            {slide.description && <p className="t26-hero__desc">{slide.description}</p>}
            {(slide.btn1_label || slide.btn2_label) && (
                <div className="t26-hero__ctas">
                    <HeroCta org={org} label={slide.btn1_label} url={slide.btn1_url} icon={slide.btn1_icon} variant="secondary" />
                    <HeroCta org={org} label={slide.btn2_label} url={slide.btn2_url} icon={slide.btn2_icon} variant="ghost" />
                </div>
            )}
        </div>
    );
}

function slideHasContent(slide) {
    if (!slide) return false;
    return !!(slide.eyebrow || slide.title || slide.subtitle || slide.description || slide.btn1_label || slide.btn2_label);
}

function HeroCarousel({ slides, context, interval = 8000 }) {
    const { org, lang } = context;
    const validSlides = slides.filter(s => s?.image);
    const [idx, setIdx] = React.useState(0);
    const [paused, setPaused] = React.useState(false);
    const total = validSlides.length;

    React.useEffect(() => {
        if (total <= 1 || paused) return;
        const t = setInterval(() => setIdx(i => (i + 1) % total), interval);
        return () => clearInterval(t);
    }, [total, paused, interval]);

    if (total === 0) return null;

    const go = (n) => setIdx(((n % total) + total) % total);
    const prev = () => go(idx - 1);
    const next = () => go(idx + 1);

    // Sticky content : on remonte jusqu'à la dernière slide avec contenu
    // (permet d'avoir des slides image-only qui laissent le texte précédent).
    const contentIndex = (() => {
        for (let k = 0; k < total; k++) {
            const i = (idx - k + total) % total;
            if (slideHasContent(applyLang(validSlides[i], lang))) return i;
        }
        return -1;
    })();

    const currentLocalized = contentIndex >= 0 ? applyLang(validSlides[contentIndex], lang) : null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: heroStyles }} />
            <section className="t26-hero">
                <div
                    className="t26-hero__frame"
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                >
                    {validSlides.map((s, i) => (
                        <div key={i} className={`t26-hero__slide ${i === idx ? "is-active" : ""}`} aria-hidden={i !== idx}>
                            <img
                                src={resolveImage(s.image)}
                                alt={applyLang(s, lang).title || ""}
                                loading={i === 0 ? "eager" : "lazy"}
                            />
                            <div className="t26-hero__wash" />
                        </div>
                    ))}

                    {total > 1 && (
                        <div className="t26-hero__count">
                            <b>{String(idx + 1).padStart(2, "0")}</b>
                            <i>/</i>
                            {String(total).padStart(2, "0")}
                        </div>
                    )}

                    {currentLocalized && (
                        <SlideContent slide={currentLocalized} org={org} contentKey={`content-${contentIndex}`} />
                    )}

                    {total > 1 && (
                        <div className="t26-hero__ctrl">
                            <div className="t26-hero__dots">
                                {validSlides.map((_, i) => (
                                    <button
                                        key={i}
                                        className={`t26-hero__dot ${i === idx ? "is-active" : ""}`}
                                        onClick={() => go(i)}
                                        aria-label={`Slide ${i + 1}`}
                                    />
                                ))}
                            </div>
                            <div className="t26-hero__arrows">
                                <button className="t26-hero__arrow" onClick={prev} aria-label={t`Précédent`}>
                                    <Icon name="chevronRight" size={18} style={{ transform: "scaleX(-1)" }} className="t26-flip-rtl" />
                                </button>
                                <button className="t26-hero__arrow" onClick={next} aria-label={t`Suivant`}>
                                    <Icon name="chevronRight" size={18} className="t26-flip-rtl" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}

/**
 * Fallback automatique : aucun slide configuré.
 * On affiche un hero compact avec org.name + config["hero.title"] + description,
 * sur un fond gradient primary/secondary.
 */
function HeroFallback({ context }) {
    const { org, config } = context;
    const title = config["hero.title"] || org.name;
    const subtitle = config["hero.subtitle"] || org.ville;
    const description = config["hero.description"] || null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: heroStyles }} />
            <section className="t26-hero t26-hero--fallback">
                <div className="t26-hero__frame">
                    <div className="t26-hero__content t26-hero__content--enter">
                        <span className="t26-hero__eyebrow">
                            <b>{org.name}</b>
                        </span>
                        <h1 className="t26-hero__title">
                            {title}
                            {subtitle && <> <em>{subtitle}</em></>}
                        </h1>
                        {description && <p className="t26-hero__desc">{description}</p>}
                    </div>
                </div>
            </section>
        </>
    );
}

/**
 * Composant Hero principal :
 * - si `config["hero.slides"]` contient au moins une slide avec image → carrousel
 * - sinon → fallback automatique (titre/description config + fond gradient)
 *
 * Header Ummah-like : pas de switch manuel, le rendu s'adapte au contenu.
 */
export default function Hero({ context }) {
    const slides = readHeroSlides(context.config);
    const validSlides = slides.filter(s => s?.image).map(s => applyLang(s, context.lang));
    if (validSlides.length === 0) return <HeroFallback context={context} />;
    return <HeroCarousel slides={validSlides} context={context} />;
}
